import styled from '@emotion/styled';
import SimpleBar from 'simplebar-react';
import { Outlet } from 'react-router-dom';
import Card from 'storybookConfig/stories/cells/Card';
import Menu from 'storybookConfig/stories/cells/Menu';
import Heading from 'storybookConfig/stories/molecules/Heading';
import { ThemeBreakpoints, ThemeColors } from 'storybookConfig/stories/theme';
import type { IconName } from 'storybookConfig/mixins/icons';

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & #settings-header-dropdown {
    font-size: 1rem;
    font-weight: 400;
  }
`;

export const SettingsFooter = styled.footer`
  display: flex;
  align-items: center;
  width: 100%;
  height: 64px;
  padding: 0rem 1.5rem;
  border-top: 1px solid ${ThemeColors.border};
`;

const ScrollWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

interface SettingsSectionTitleProps {
  children: React.ReactNode;
  as?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const SectionTitleWrapper = styled.div`
  margin-bottom: 4px;
`;

export const SettingsSectionTitle = ({ children, as = 'h4' }: SettingsSectionTitleProps) => (
  <SectionTitleWrapper>
    <Heading variant="Headings/H4" as={as}>
      {children}
    </Heading>
  </SectionTitleWrapper>
);

export const SettingsDescription = styled.p`
  font-size: 0.875rem;
  color: ${ThemeColors.bodyTextCaption};
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

interface SettingsPageHeaderProps {
  title: string;
  children?: React.ReactNode;
}

const PAGE_HEADER_MARGIN_BOTTOM = 16;

export const SettingsPageHeader = ({ title, children }: SettingsPageHeaderProps) => {
  return (
    <>
      <Header className="d-flex justify-space-between align-items-center">
        <div style={{ marginRight: 8 }}>
          <Heading variant="Headings/H3" as="h3">
            {title}
          </Heading>
        </div>

        {children && children}
      </Header>
      <hr style={{ marginBottom: PAGE_HEADER_MARGIN_BOTTOM }} />
    </>
  );
};

interface SettingsMainProps {
  children: React.ReactNode;
}

const SETTINGS_CONTENT_PADDING = 24;

export const SettingsMain = ({ children }: SettingsMainProps) => (
  <ScrollWrapper>
    <SimpleBar style={{ height: '100%' }}>
      <div style={{ padding: SETTINGS_CONTENT_PADDING }}>{children}</div>
    </SimpleBar>
  </ScrollWrapper>
);

// SettingsSubLayout — responsive two-panel layout used by every settings tab

export interface SettingsSubMenuItem {
  label: string;
  iconName: IconName;
  to: string;
  isActive: boolean;
}

const SubLayoutGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media screen and (${ThemeBreakpoints.lg}) {
    display: grid;
    grid-template-columns: 240px 1fr;
    align-items: start;
  }
`;

const MenuWrapper = styled(Card)`
  padding: 24px;

  @media screen and (${ThemeBreakpoints.lg}) {
    position: sticky;
    top: 24px;
  }
`;

interface SettingsSubLayoutProps {
  items: SettingsSubMenuItem[];
}

export const SettingsSubLayout = ({ items }: SettingsSubLayoutProps) => (
  <SubLayoutGrid>
    <MenuWrapper>
      <Menu>
        {items.map((item) => (
          <Menu.Link key={item.to} $iconName={item.iconName} $isActive={item.isActive} to={item.to}>
            {item.label}
          </Menu.Link>
        ))}
      </Menu>
    </MenuWrapper>

    <Card>
      <Outlet />
    </Card>
  </SubLayoutGrid>
);
